<template>
  <div class="lg-page">
    <div class="lg-shell">
      <div class="lg-brand">
        <img class="lg-logo" src="../../assets/logo.png" alt="LiveRun">
        <span class="lg-env-badge">Console</span>
      </div>

      <div class="lg-card">
        <div class="pg-title-row">
          <div>
            <h1 class="pg-title">Sign in</h1>
            <p class="pg-title-sub">Access trainer tools, live race controls and console settings</p>
          </div>
        </div>

        <div class="lg-header">
          <span class="lg-icon">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
              <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"/>
              <polyline points="10 17 15 12 10 7"/>
              <line x1="15" y1="12" x2="3" y2="12"/>
            </svg>
          </span>
          <div>
            <h2 class="lg-title">Trainer Access</h2>
            <p class="lg-subtitle">Use your LiveRun trainer credentials to continue</p>
          </div>
        </div>

        <div class="lg-checking" v-if="checkingRememberedSession">
          <div class="lg-sk-row">
            <div class="lg-sk-icon sk-shimmer"></div>
            <div class="lg-sk-copy">
              <div class="lg-sk-line lg-sk-line--wide sk-shimmer"></div>
              <div class="lg-sk-line sk-shimmer"></div>
            </div>
          </div>
          <div class="lg-sk-field sk-shimmer"></div>
          <div class="lg-sk-field sk-shimmer"></div>
          <div class="lg-sk-button sk-shimmer"></div>
          <p class="lg-checking-text">Checking saved session...</p>
        </div>

        <form class="lg-form" @submit="handleSubmit($event)" novalidate v-else>
          <div class="lg-alert" v-if="error" v-html="error"></div>
          <div class="lg-alert" v-if="!trainer"><strong>Failed to sign in!</strong> Only trainers are allowed to access LiveRun Console.</div>

          <div class="lg-field">
            <label class="lg-label" for="username">Username <span class="lg-required">*</span></label>
            <div class="lg-input-wrap" :class="{ 'lg-input-wrap--danger': errors.has('username') }">
              <svg class="lg-input-icon" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
                <path d="M20 21a8 8 0 0 0-16 0"/>
                <circle cx="12" cy="7" r="4"/>
              </svg>
              <input
                id="username"
                v-validate
                data-vv-rules="required"
                class="lg-input"
                type="text"
                name="username"
                v-model="user.username"
                autocomplete="username"
                required>
            </div>
            <span v-show="errors.has('username')" class="lg-error">Fill in the username</span>
          </div>

          <div class="lg-field">
            <label class="lg-label" for="password">Password <span class="lg-required">*</span></label>
            <div class="lg-input-wrap" :class="{ 'lg-input-wrap--danger': errors.has('password') }">
              <svg class="lg-input-icon" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
                <rect x="3" y="11" width="18" height="10" rx="2"/>
                <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
              </svg>
              <input
                id="password"
                v-validate
                data-vv-rules="required"
                class="lg-input"
                type="password"
                name="password"
                v-model="user.password"
                autocomplete="current-password"
                required>
            </div>
            <span v-show="errors.has('password')" class="lg-error">Fill in the password!</span>
          </div>

          <div class="lg-options">
            <div class="lg-toggle-row">
              <lv-toggle v-model="rememberMe" @click.native.prevent></lv-toggle>
              <span class="lg-toggle-label">Remember Me</span>
              <span class="lg-toggle-badge">{{ rememberMe ? 'On' : 'Off' }}</span>
            </div>

            <router-link to="" class="lg-forgot" slot="actions">Forgot password?</router-link>
          </div>

          <button id="sign-in" class="lg-submit" :disabled="signingIn">
            <svg v-if="!signingIn" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.1" stroke-linecap="round" stroke-linejoin="round">
              <path d="M5 12h14"/>
              <path d="M12 5l7 7-7 7"/>
            </svg>
            <span v-else class="lg-spinner"></span>
            {{ signingIn ? 'Signing in...' : 'Sign In' }}
          </button>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import LvToggle from '@/components/Forms/LvToggle'
import {
  clearRememberMeToken,
  clearStoredAuthToken,
  getRememberMeToken,
  restoreRememberMeToken,
  saveRememberMeToken
} from '@/utils/rememberMeAuth'

export default {
  name: 'login',
  components: {
    LvToggle
  },
  data () {
    return {
      trainer:true,
      user: {
        username: null,
        password: null
      },
      rememberMe: true,
      checkingRememberedSession: false,
      signingIn: false,
      error: ''
    }
  },
  computed: {
    ...mapGetters({
      istrainer: 'Trainer/istrainer'
    })
  },
  methods: {
    initialize () {
      this.trainer = this.istrainer
    },
    initializeRememberedSession () {
      if (!getRememberMeToken()) {
        return
      }

      restoreRememberMeToken()
      this.checkingRememberedSession = true

      this.$auth.fetch({
        success: function () {
          this.checkingRememberedSession = false

          if (this.$auth.check()) {
            this.setIsTrainer(true)
            this.$router.replace('/')
          }
        }.bind(this),
        error: function () {
          clearRememberMeToken()
          clearStoredAuthToken()
          this.checkingRememberedSession = false
        }.bind(this)
      })
    },
    handleSubmit (e) {
      e.preventDefault()

      this.$validator.validateAll()
        .then(success => {
          if (!success) {
            return
          }

          this.loadingSignInButton()

          this.$auth.login({
            body: this.user,
            rememberMe: this.rememberMe,
            success: function(res) {
              this.error = ''
              this.resetSignInButton()
              this.setIsTrainer(true)
              
              // If rememberMe is checked, store the token in cookies for 1 year
              if (this.rememberMe) {
                saveRememberMeToken(localStorage.getItem('default-auth-token'))
              } else {
                clearRememberMeToken()
              }
            }.bind(this),
            error: function(res) {
              this.error = '<strong>Failed to sign in!</strong> Please check your credentials and try again.'
              this.resetSignInButton()
            }.bind(this)
          })
        })
    },
    loadingSignInButton () {
      this.signingIn = true
    },
    resetSignInButton () {
      this.signingIn = false
    },
    ...mapActions({
      setIsTrainer: 'Trainer/setIsTrainer'
    })
  },
  created () {
    this.initialize()
    this.initializeRememberedSession()
  },
}
</script>

<style>
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;600;700;900&display=swap');
</style>

<style scoped>
.lg-page {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  background: #423799;
}

.lg-shell {
  width: min(100%, 430px);
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.lg-brand {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
}

.lg-logo {
  width: 88px;
  height: auto;
  display: block;
  filter: drop-shadow(0 8px 20px rgba(0, 0, 0, 0.18));
}

.lg-env-badge {
  display: inline-flex;
  align-items: center;
  height: 26px;
  padding: 0 10px;
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, 0.28);
  background: rgba(255, 255, 255, 0.12);
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0;
  text-transform: uppercase;
}

.lg-card {
  background: #fff;
  border: 1px solid #e8e8ed;
  border-radius: 14px;
  padding: 1.5rem 1.75rem;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.05);
}

.pg-title-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 1rem;
  border-bottom: 1px solid #f0f0f5;
  margin-bottom: 1.5rem;
}

.pg-title {
  font-family: 'Inter', sans-serif;
  font-size: 34px;
  font-weight: 900;
  letter-spacing: -0.04em;
  color: #1a1a2e;
  line-height: 1.1;
  margin: 0;
}

.pg-title-sub {
  font-size: 13px;
  color: #8a8a9a;
  font-weight: 400;
  margin: 5px 0 0;
  line-height: 1.4;
}

.lg-header {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 1.25rem;
}

.lg-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: #f0effe;
  color: #5b4fcf;
  flex-shrink: 0;
  margin-top: 2px;
}

.lg-title {
  font-size: 15px;
  font-weight: 700;
  color: #1a1a2e;
  margin: 0 0 3px;
  letter-spacing: 0;
}

.lg-subtitle {
  font-size: 12px;
  color: #8a8a9a;
  margin: 0;
  line-height: 1.45;
}

.lg-form {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.lg-alert {
  border: 1px solid rgba(192, 57, 43, 0.16);
  background: rgba(192, 57, 43, 0.08);
  color: #c0392b;
  border-radius: 9px;
  padding: 0.75rem 0.85rem;
  font-size: 12px;
  line-height: 1.4;
}

.lg-field {
  display: flex;
  flex-direction: column;
  gap: 7px;
}

.lg-label {
  font-size: 12px;
  line-height: 1.2;
  font-weight: 600;
  color: #1a1a2e;
  margin: 0;
}

.lg-required {
  color: #c0392b;
}

.lg-input-wrap {
  min-height: 42px;
  display: flex;
  align-items: center;
  gap: 9px;
  border: 1px solid #e8e8ed;
  border-radius: 9px;
  background: #fafafa;
  padding: 0 12px;
  transition: border-color 0.15s, box-shadow 0.15s, background 0.15s;
}

.lg-input-wrap:focus-within {
  border-color: #5b4fcf;
  background: #fff;
  box-shadow: 0 0 0 3px rgba(91, 79, 207, 0.11);
}

.lg-input-wrap--danger {
  border-color: rgba(192, 57, 43, 0.55);
}

.lg-input-icon {
  color: #8a8a9a;
  flex-shrink: 0;
}

.lg-input {
  width: 100%;
  min-width: 0;
  border: 0;
  outline: 0;
  background: transparent;
  color: #1a1a2e;
  font-size: 13px;
  line-height: 1.3;
  padding: 0;
}

.lg-error {
  font-size: 12px;
  color: #c0392b;
  line-height: 1.2;
}

.lg-options {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
}

.lg-toggle-row {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: 28px;
}

.lg-toggle-label {
  color: #1a1a2e;
  font-size: 12px;
  font-weight: 600;
  margin: 0;
}

.lg-toggle-badge {
  display: inline-flex;
  align-items: center;
  height: 22px;
  padding: 0 8px;
  border-radius: 999px;
  background: #f0effe;
  color: #413b94;
  font-size: 11px;
  font-weight: 700;
}

.lg-toggle-row .lv-toggle {
  width: 42px;
  height: 23px;
}

.lg-toggle-row .lv-toggle .lv-thumb {
  width: 19px;
  height: 19px;
}

.lg-toggle-row .lv-toggle.on .lv-thumb {
  transform: translateX(19px);
}

.lg-toggle-row .lv-toggle,
.lg-forgot,
.lg-submit {
  cursor: pointer;
}

.lg-toggle-row .lv-toggle:disabled,
.lg-submit:disabled {
  cursor: not-allowed;
}

.lg-forgot {
  color: #5b4fcf;
  font-size: 12px;
  font-weight: 700;
  text-decoration: none;
}

.lg-forgot:hover,
.lg-forgot:focus {
  color: #4338ca;
  text-decoration: underline;
}

.lg-submit {
  width: 100%;
  height: 36px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border: none;
  border-radius: 8px;
  background: #5b4fcf;
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s, box-shadow 0.15s, transform 0.15s;
}

.lg-submit:hover:not(:disabled),
.lg-submit:focus:not(:disabled) {
  background: #4338ca;
  box-shadow: 0 4px 16px rgba(91, 79, 207, 0.32);
  transform: translateY(-1px);
}

.lg-submit:disabled {
  cursor: not-allowed;
  opacity: 0.74;
}

.lg-spinner {
  width: 13px;
  height: 13px;
  border-radius: 50%;
  border: 2px solid rgba(255, 255, 255, 0.45);
  border-top-color: #fff;
  animation: lg-spin 0.75s linear infinite;
}

.lg-checking {
  display: flex;
  flex-direction: column;
  gap: 0.9rem;
}

.lg-sk-row {
  display: flex;
  gap: 12px;
  align-items: center;
}

.lg-sk-icon {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  flex-shrink: 0;
}

.lg-sk-copy {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.lg-sk-line {
  width: 48%;
  height: 10px;
}

.lg-sk-line--wide {
  width: 72%;
}

.lg-sk-field {
  width: 100%;
  height: 42px;
  border-radius: 9px;
}

.lg-sk-button {
  width: 100%;
  height: 36px;
  border-radius: 8px;
}

.lg-checking-text {
  font-size: 12px;
  color: #8a8a9a;
  margin: 0;
  text-align: center;
}

.sk-shimmer {
  background: linear-gradient(90deg, #f0f0f5 25%, #e4e4ef 50%, #f0f0f5 75%);
  background-size: 600px 100%;
  animation: sk-shimmer 1.4s ease-in-out infinite;
  border-radius: 6px;
}

@keyframes sk-shimmer {
  0% {
    background-position: -600px 0;
  }

  100% {
    background-position: 600px 0;
  }
}

@keyframes lg-spin {
  to {
    transform: rotate(360deg);
  }
}

@media (max-width: 520px) {
  .lg-page {
    align-items: flex-start;
    padding: 1rem;
  }

  .lg-card {
    padding: 1.25rem;
  }

  .pg-title {
    font-size: 30px;
  }

  .lg-options {
    align-items: flex-start;
    flex-direction: column;
  }
}
</style>



// WEBPACK FOOTER //
// Login.vue?d6a9221a